import React from 'react';
import get from 'lodash.get';
import isNil from 'lodash.isnil';
import isEmpty from 'lodash.isempty';
import { TextCell } from '../../components/cell/shared/Cells';
import ScoreInterpretationMapper from '../../constants/shared/ScoreInterpretationMapper';
import {
    getCommonGradeHeaderCell,
    getCompositeCell,
    getDividedColSpan,
    getStudentNameCommonCell,
    getTwoPartHeaderCell,
    getProgressGridHeader,
    getBenchmarkScoreCell,
    buildDividedColSpan,
    getReverseThresholds,
} from '../../features/progress/shared/progressHelper';
import { COMPOSITE, TITLE, PERIOD, GATING, GA } from '../../constants/shared/CommonConstants';
import {
    SECTION,
    ACHIEVED_RATE,
    ACHIEVED_RATE_LEVEL,
    GROWTH_RATE_LEVELS,
    TITLE as GROWTH_ANALYSIS_TITLE,
    INDEX as GROWTH_ANALYSIS_INDEX,
} from '../../constants/shared/GrowthAnalysisConstants';
import { KEY, TITLE as PM_TITLES, PM_ROW_SPAN } from '../../constants/shared/PMConstants';
import { CLASS_NAME, INDEX as CELL_INDEX, GATING_MAP } from '../../constants/shared/CellConstants';
import { GROWTH_RATE as ZOG_GROWTH_RATE } from '../../constants/shared/ZoGConstants';
import { MEASURE } from '../../constants/shared/MeasuresConstants';
import { isMeasureAvailableInGrade } from './gradeToMeasureUtils';
import { createDataTourStep } from './demoModeUtils';
import {
    getGoalByMeasureYearGradePeriod,
    getStudentByMeasureYearPeriodGrade,
    getStudentResultByMeasureYearGradePeriod,
} from './filterUtils';
import { getCursorPointerClassName } from './pmUtils';
import { sortByPredefinedOrder } from './sortUtils';
import { LECTURA_ZOG_MEASURES } from '../../constants/espanol/MeasuresConstants';

const { ABOVE_TITLE, BENCHMARK_TITLE } = TITLE;
const { EOY, BOY } = PERIOD;
const { SECOND_COLUMN, SORTING_SPACE, LEFT_ALIGN } = CLASS_NAME;
const { AVERAGE, WELL_BELOW_AVERAGE, BELOW_AVERAGE } = ZOG_GROWTH_RATE;
const { FIRST_ARRAY_INDEX, ZERO_SCORE_VALUE, FIRST_GRID_ROW, START_FIRST_COL_SPAN_INDEX } =
    CELL_INDEX;
const {
    BENCHMARK_COL_SPAN_START_INDEX,
    BENCHMARK_COL_SPAN_END_INDEX,
    GROWTH_COL_SPAN_START_INDEX,
    GROWTH_COL_SPAN_END_INDEX,
    GOAL_COL_SPAN_START_INDEX,
    GROWTH_ANALYSIS_COLUMN_COUNT,
} = GROWTH_ANALYSIS_INDEX;
const { GROWTH_TITLE, BENCHMARK_SCORES_TITLE, GOAL_TITLE } = GROWTH_ANALYSIS_TITLE;
const { ACHIEVED, MET, GROWTH_SECTIONS, GOAL_SECTIONS } = SECTION;
const { YEAR_GROWTH_RATE, GROWTH_RATE } = ACHIEVED_RATE;
const { YEAR_GROWTH_RATE_LEVEL, GROWTH_RATE_LEVEL } = ACHIEVED_RATE_LEVEL;
const { OVERALL } = MEASURE;

export const addPlusSign = value => (value > ZERO_SCORE_VALUE ? `+${value}` : value);

export const getGrowthLevel = growthLevel =>
    growthLevel ? growthLevel.replace(` ${GROWTH_TITLE}`, '') : '';

export const getNeededObject = (goal, zone, isWholeYear) => {
    if (isNil(goal) || isNil(zone)) {
        return {};
    }

    const { score, growthRates, yearGrowthRates } = zone;
    const rates = isWholeYear ? yearGrowthRates : growthRates;
    const neededValue = goal - score;
    return {
        neededGrowthRate: neededValue,
        neededGrowthRateLevel: getGrowthLevel(
            get(
                rates.find(({ maxGain }) => neededValue <= maxGain || isNil(maxGain)),
                'growthLevel',
            ),
        ),
    };
};

export const getStudentOverallObject = (results, studentId, year, period, grade) => {
    const overall = getStudentByMeasureYearPeriodGrade(
        results,
        studentId,
        OVERALL,
        year,
        period,
        grade,
    );
    return {
        interp: get(overall, 'interp'),
        score: get(overall, 'score'),
        isSecure: !!get(overall, 'isSecure'),
    };
};

export const getCompositeAchievedGrowthRateLevel = (
    isTestedOutAvailable,
    currentComposite,
    nextComposite,
    zone,
    isWholeYear,
) => {
    const currentCompositeInterp = ScoreInterpretationMapper[currentComposite.interp];
    const nextCompositeInterp = ScoreInterpretationMapper[nextComposite.interp];
    const achievedGrowthRateLevel = getGrowthLevel(
        get(zone, isWholeYear ? YEAR_GROWTH_RATE_LEVEL : GROWTH_RATE_LEVEL, ''),
    );
    const compositeInterps = [ABOVE_TITLE, BENCHMARK_TITLE];
    const compositeGrowthes = [WELL_BELOW_AVERAGE, BELOW_AVERAGE];

    return isTestedOutAvailable &&
        compositeInterps.includes(currentCompositeInterp) &&
        compositeInterps.includes(nextCompositeInterp) &&
        compositeGrowthes.includes(achievedGrowthRateLevel)
        ? AVERAGE
        : achievedGrowthRateLevel;
};

export const getAchievedGrowthObject = (zone, isWholeYear) => ({
    achievedGrowthRate: get(zone, isWholeYear ? YEAR_GROWTH_RATE : GROWTH_RATE),
    achievedGrowthRateLevel: getGrowthLevel(
        get(zone, isWholeYear ? YEAR_GROWTH_RATE_LEVEL : GROWTH_RATE_LEVEL, ''),
    ),
});

export const getInterpByGoal = (goal, thresholds) =>
    ScoreInterpretationMapper[
        get(thresholds.filter(({ scoreThreshold }) => scoreThreshold <= goal).pop(), 'interp', '')
    ];

export const isFullYear = (startPeriod, endPeriod) => startPeriod === BOY && endPeriod === EOY;

export const getGrowthAnalysisData = ({
    statuses,
    growthZones,
    interps,
    studentGoals,
    year,
    grade,
    startPeriod,
    endPeriod,
    getGradeToMeasureMapper,
    getMeasureByGrade,
}) =>
    sortByPredefinedOrder(
        getMeasureByGrade(grade, false, getGradeToMeasureMapper).map(measure => {
            const statusByStartPeriod = getStudentResultByMeasureYearGradePeriod(
                statuses,
                measure,
                year,
                grade,
                startPeriod,
            );
            const statusByEndPeriod = getStudentResultByMeasureYearGradePeriod(
                statuses,
                measure,
                year,
                grade,
                endPeriod,
            );
            const startZone = getStudentResultByMeasureYearGradePeriod(
                growthZones,
                measure,
                year,
                null,
                startPeriod,
            );
            const customGoal = getGoalByMeasureYearGradePeriod(
                studentGoals,
                measure,
                year,
                grade,
                endPeriod,
            );
            const thresholds = getReverseThresholds(interps, measure, grade, endPeriod);

            return {
                measure,
                statusByStartPeriod,
                statusByEndPeriod,
                goalObject: {
                    customGoal,
                    interp: getInterpByGoal(customGoal, thresholds),
                },
                achievedObject: getAchievedGrowthObject(
                    getStudentResultByMeasureYearGradePeriod(
                        growthZones,
                        measure,
                        year,
                        null,
                        endPeriod,
                    ),
                    isFullYear(startPeriod, endPeriod),
                ),
                neededObject: getNeededObject(
                    customGoal,
                    startZone,
                    isFullYear(startPeriod, endPeriod),
                ),
            };
        }),
        LECTURA_ZOG_MEASURES,
        'measure',
    );

const buildStudentRow = ({
    grade,
    studentId,
    dataByMeasure = {},
    openProbeDetails,
    studentInfo = {},
    currentComposite = {},
    nextComposite = {},
    location = {},
    compositeGrowth = {},
    getGradeToMeasureMapper,
    appSpecificParams,
}) => {
    const {
        measure,
        statusByStartPeriod = {},
        statusByEndPeriod = {},
        goalObject = {},
        achievedObject = {},
        neededObject = {},
    } = dataByMeasure;
    const { customGoal, interp } = goalObject;
    const { neededGrowthRate, neededGrowthRateLevel } = neededObject;
    const { achievedGrowthRate, achievedGrowthRateLevel } = achievedObject;
    const {
        achievedGrowthRate: compositeAchievedGrowthRate,
        achievedGrowthRateLevel: compositeAchievedGrowthRateLevel,
    } = compositeGrowth;
    const { SkillCell, CommonCellBuilder, ssUrl } = appSpecificParams;

    const startResultId = get(statusByStartPeriod, 'resultId');
    const endResultId = get(statusByEndPeriod, 'resultId');
    const commonBenchmarkParams = {
        externalId: studentId,
        measure,
        grade,
        location,
        isCellDisabled: !isMeasureAvailableInGrade(getGradeToMeasureMapper, grade, measure),
    };
    const isEmptyData = isEmpty(dataByMeasure);
    const startBenchmarkScoreParams = {
        ...commonBenchmarkParams,
        statusForMeasureYearPeriod: statusByStartPeriod,
        period: statusByStartPeriod.period,
        optionalCellData: {
            classNames: `${getCursorPointerClassName(startResultId)}`,
        },
        openPanel: isNil(startResultId) ? () => {} : openProbeDetails,
    };
    const endBenchmarkScoreParams = {
        ...commonBenchmarkParams,
        statusForMeasureYearPeriod: statusByEndPeriod,
        period: statusByEndPeriod.period,
        optionalCellData: {
            classNames: `${getCursorPointerClassName(endResultId)}`,
        },
        openPanel: isNil(endResultId) ? () => {} : openProbeDetails,
    };
    const isTestedOut = statusByEndPeriod.gatingType === GATING.TESTED_OUT;

    const getCellContent = cellType => {
        if (cellType === MET && !isTestedOut) {
            return (
                <div
                    className={`${
                        statusByEndPeriod.score >= customGoal ? 'icon-check' : 'icon-not-allowed'
                    } growth`}
                />
            );
        }

        if (cellType === ACHIEVED && !isTestedOut) {
            return (
                <>
                    <span>{addPlusSign(achievedGrowthRate)}</span>
                    <span>{achievedGrowthRateLevel}</span>
                </>
            );
        }

        return <span>{GATING_MAP[statusByEndPeriod.gatingType]}</span>;
    };

    return {
        ...getStudentNameCommonCell(studentInfo, location, ssUrl.PROGRESS),
        ...getCompositeCell('compositeScoreForStartPeriod', currentComposite),
        ...getCompositeCell('compositeScoreForEndPeriod', nextComposite),
        compositeGrowth: isEmpty(compositeGrowth) ? (
            <TextCell classNames={PM_ROW_SPAN} isDataCell={false} />
        ) : (
            <TextCell
                classNames="achieved-cell"
                content={
                    <>
                        <span>{addPlusSign(compositeAchievedGrowthRate)}</span>
                        <span>{compositeAchievedGrowthRateLevel}</span>
                    </>
                }
            >
                {GROWTH_RATE_LEVELS.indexOf(compositeAchievedGrowthRateLevel)}
            </TextCell>
        ),
        skill: <SkillCell measure={measure} />,
        ...getBenchmarkScoreCell(
            'startBenchmarkScore',
            startBenchmarkScoreParams,
            isEmptyData,
            CommonCellBuilder,
        ),
        ...getBenchmarkScoreCell(
            'endBenchmarkScore',
            endBenchmarkScoreParams,
            isEmptyData,
            CommonCellBuilder,
        ),
        needed: (
            <TextCell
                classNames="needed-cell"
                content={
                    <>
                        <span>{addPlusSign(neededGrowthRate)}</span>
                        <span>{neededGrowthRateLevel}</span>
                    </>
                }
            />
        ),
        achieved: (
            <TextCell
                title={createDataTourStep(achievedGrowthRate)}
                classNames={`achieved-cell ${isTestedOut && 'gating-cell'}`}
                content={getCellContent(ACHIEVED)}
            />
        ),
        set: (
            <TextCell
                classNames="set-goal-cell"
                content={
                    <>
                        <span>{customGoal}</span>
                        <span>{interp}</span>
                    </>
                }
            />
        ),
        met: (
            <TextCell
                classNames={`met-icon ${isTestedOut && 'gating-cell'}`}
                content={isEmpty(statusByEndPeriod) ? null : getCellContent(MET)}
            />
        ),
    };
};

const getHeaderRow = ({ startPeriod, endPeriod }, grade) => ({
    ...getCommonGradeHeaderCell(grade),
    ...getTwoPartHeaderCell(
        'compositeScoreForStartPeriod',
        SECOND_COLUMN,
        '',
        startPeriod,
        COMPOSITE,
        true,
    ),
    ...getTwoPartHeaderCell(
        'compositeScoreForEndPeriod',
        SECOND_COLUMN,
        '',
        endPeriod,
        COMPOSITE,
        true,
    ),
    ...getTwoPartHeaderCell('compositeGrowth', SECOND_COLUMN, '', COMPOSITE, GROWTH_TITLE, true),
    ...getTwoPartHeaderCell(KEY.SKILL_KEY, LEFT_ALIGN, SORTING_SPACE, '', PM_TITLES.SKILL_TITLE),
    startBenchmarkScore: {},
    endBenchmarkScore: {},
    needed: {},
    achieved: {},
    set: {},
    met: {},
});

const getStudentRows = (
    grade,
    { studentInfo, data, currentComposite, nextComposite, compositeGrowth },
    location,
    openProbeDetails,
    getGradeToMeasureMapper,
    appSpecificParams,
) => {
    if (isEmpty(data)) {
        return Array.of(
            buildStudentRow({
                grade,
                studentId: studentInfo.id,
                openProbeDetails,
                getGradeToMeasureMapper,
                appSpecificParams,
            }),
        );
    }

    return data.map((dataByMeasure, index) => {
        const isFirstIndex = index === FIRST_ARRAY_INDEX;
        const { id: studentId } = studentInfo;

        let studentRowData = {
            grade,
            studentId,
            dataByMeasure,
            openProbeDetails,
            getGradeToMeasureMapper,
            appSpecificParams,
        };

        if (isFirstIndex) {
            studentRowData = {
                ...studentRowData,
                studentInfo,
                currentComposite,
                nextComposite,
                location,
                compositeGrowth,
                getGradeToMeasureMapper,
                appSpecificParams,
            };
        }

        return buildStudentRow(studentRowData);
    });
};

const buildRowsForGrade = (
    { grade, studentsData },
    periodObject,
    location,
    openProbeDetails,
    gradeToMeasureMapper,
    appSpecificParams,
) => {
    const studentsRowObjects = [];

    studentsData.forEach(studentData => {
        const studentRows = getStudentRows(
            grade,
            studentData,
            location,
            openProbeDetails,
            gradeToMeasureMapper,
            appSpecificParams,
        );
        const studentRowObjects = studentRows.map(studentRow => ({
            rowObject: studentRow,
        }));
        studentsRowObjects.push(studentRowObjects);
    });
    return [
        [
            {
                isSubheader: true,
                rowObject: getHeaderRow(periodObject, grade),
            },
        ],
        ...studentsRowObjects,
    ];
};

export const getRowsToRender = ({
    resultsByGrades,
    periodObject,
    location,
    openProbeDetails,
    gradeToMeasureMapper,
    appSpecificParams,
}) => {
    const rowsToRenderObject = {};
    const studentRowsByGrades = [];

    resultsByGrades.forEach(gradeResult => {
        studentRowsByGrades.push(
            ...buildRowsForGrade(
                gradeResult,
                periodObject,
                location,
                openProbeDetails,
                gradeToMeasureMapper,
                appSpecificParams,
            ),
        );
    });
    rowsToRenderObject.rows = [
        [
            {
                isGridHeader: true,
                isSubheader: true,
                rowObject: {},
            },
        ],
        ...studentRowsByGrades,
    ];
    rowsToRenderObject.colSpans = [
        getProgressGridHeader(
            FIRST_GRID_ROW,
            START_FIRST_COL_SPAN_INDEX,
            GROWTH_ANALYSIS_COLUMN_COUNT,
            GA.GROWTH_ANALYSIS,
        ),
        ...getDividedColSpan(
            resultsByGrades,
            BENCHMARK_COL_SPAN_START_INDEX,
            BENCHMARK_COL_SPAN_END_INDEX,
            buildDividedColSpan(BENCHMARK_SCORES_TITLE, Object.values(periodObject)),
        ),
        ...getDividedColSpan(
            resultsByGrades,
            GROWTH_COL_SPAN_START_INDEX,
            GROWTH_COL_SPAN_END_INDEX,
            buildDividedColSpan(GROWTH_TITLE, GROWTH_SECTIONS),
        ),
        ...getDividedColSpan(
            resultsByGrades,
            GOAL_COL_SPAN_START_INDEX,
            GROWTH_ANALYSIS_COLUMN_COUNT,
            buildDividedColSpan(GOAL_TITLE, GOAL_SECTIONS),
        ),
    ];
    return rowsToRenderObject;
};
